import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  MenuItem,
  Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const EXPENSE_TYPES = [
  'sales channel',
  'inventory',
  'supplies',
  'other',
];

const ExpenseForm = ({
  expense,
  onSubmit,
  onCancel,
  isSubmitting,
  channels = [],
}) => {
  const [validationError, setValidationError] = useState('');
  const [formData, setFormData] = useState({
    expenseType: '',
    expenseAmount: '',
    expenseDate: new Date().toISOString().split('T')[0],
    expenseNotes: '',
    salesChannel: '',
  });

  useEffect(() => {
    if (expense) {
      const toDateInputValue = (value) => {
        if (!value) return new Date().toISOString().split('T')[0];

        if (typeof value.toDate === 'function') {
          const date = value.toDate();
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }

        if (value._seconds) {
          const date = new Date(value._seconds * 1000);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }

        if (typeof value === 'string') {
          const hasTimeZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
          const hasTime = value.includes('T');
          if (hasTime && hasTimeZone) {
            const parsed = new Date(value);
            if (!Number.isNaN(parsed.getTime())) {
              const year = parsed.getFullYear();
              const month = String(parsed.getMonth() + 1).padStart(2, '0');
              const day = String(parsed.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
          }
          return value.split('T')[0];
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
          return new Date().toISOString().split('T')[0];
        }
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const expenseDate = toDateInputValue(expense.expenseDate);

      setFormData({
        expenseType: expense.expenseType || '',
        expenseAmount: expense.expenseAmount || '',
        expenseDate: expenseDate,
        expenseNotes: expense.expenseNotes || '',
        salesChannel: expense.salesChannel || '',
      });
    }
  }, [expense]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'expenseType' && value !== 'sales channel' ? { salesChannel: '' } : null),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.expenseType) {
      setValidationError('Please select an expense type');
      return;
    }

    if (!formData.expenseAmount || parseFloat(formData.expenseAmount) < 0) {
      setValidationError('Please enter a valid expense amount');
      return;
    }

    if (!formData.expenseDate) {
      setValidationError('Please select an expense date');
      return;
    }

    if (formData.expenseType === 'sales channel' && !formData.salesChannel) {
      setValidationError('Please select a sales channel');
      return;
    }

    onSubmit({
      ...formData,
      expenseAmount: parseFloat(formData.expenseAmount),
      expenseDate: formData.expenseDate,
      salesChannel: formData.expenseType === 'sales channel' ? formData.salesChannel : null,
    }, expense?.id);
  };

  return (
    <Box sx={{ p: 2, minWidth: 350 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {expense ? 'Edit Expense' : 'Add New Expense'}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {/* Expense Type */}
          <TextField
            select
            fullWidth
            label="Expense Type"
            name="expenseType"
            value={formData.expenseType}
            onChange={handleInputChange}
            required
            size="small"
            InputLabelProps={{ htmlFor: undefined }}
          >
            {EXPENSE_TYPES.map((type) => (
              <MenuItem key={type} value={type} sx={{ textTransform: 'capitalize' }}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </MenuItem>
            ))}
          </TextField>

          {/* Amount */}
          <TextField
            fullWidth
            label="Amount ($)"
            name="expenseAmount"
            type="number"
            value={formData.expenseAmount}
            onChange={handleInputChange}
            inputProps={{ min: '0', step: '0.01' }}
            required
            size="small"
          />

          {formData.expenseType === 'sales channel' && (
            <TextField
              select
              fullWidth
              label="Sales Channel"
              name="salesChannel"
              value={formData.salesChannel}
              onChange={handleInputChange}
              required
              size="small"
              disabled={channels.length === 0}
              InputLabelProps={{ htmlFor: undefined }}
            >
              {channels.length === 0 ? (
                <MenuItem value="" disabled>
                  No sales channels available
                </MenuItem>
              ) : (
                channels.map((channel) => (
                  <MenuItem key={channel.id} value={channel.id}>
                    {channel.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          )}

          {/* Date */}
          <TextField
            fullWidth
            label="Expense Date"
            name="expenseDate"
            type="date"
            value={formData.expenseDate}
            onChange={handleInputChange}
            required
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          {/* Notes */}
          <TextField
            fullWidth
            label="Notes (Optional)"
            name="expenseNotes"
            value={formData.expenseNotes}
            onChange={handleInputChange}
            multiline
            rows={2}
            placeholder="Add any notes about this expense"
            size="small"
          />

          {/* Validation Error */}
          {validationError && (
            <Alert severity="warning" onClose={() => setValidationError('')} sx={{ mb: 1 }}>{validationError}</Alert>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={onCancel}
              startIcon={<CancelIcon />}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              onClick={handleSubmit}
              startIcon={<SaveIcon />}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default ExpenseForm;
